<template>
  <property-report-card
    title="Fees"
    description="Compare your property’s fees to market averages."
    :printing-mode="printingMode"
  >
    <template #actions>
      <q-btn flat no-caps class="q-my-auto" @click="exportCSV">
        <q-icon name="download" class="q-mr-sm" size="medium" />Download
      </q-btn>
    </template>
    <q-table
      id="intercom-fees-content"
      :rows="rows"
      :columns="columns"
      :rows-per-page-options="[0]"
      hide-bottom
      bordered
      dense
      flat
      :table-header-class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'"
    >
      <template #body="props">
        <q-tr
          :props="props"
          :class="{
            'text-bold bg-deep-purple-light text-primary':
              subjectDetail && props.row.id === subjectDetail.id
          }"
        >
          <q-td v-for="col in props.cols" :key="col.name" :props="props">
            <q-btn
              v-if="col.name === 'fees' && props.row.fees"
              no-caps
              color="primary"
              flat
              clickable
              dense
              size="sm"
              class="q-px-sm q-pt-xs"
              @click="$tracker.openedPopup('more_fees')"
            >
              +{{ props.row.fees.length }} more
              <q-icon name="arrow_drop_down" size="small" />
              <q-menu transition-duration="100">
                <q-separator />
                <q-markup-table flat dense square>
                  <thead>
                    <tr class="text-left">
                      <th scope="col" class="text-left">Label</th>
                      <th scope="col" class="text-left">Amount</th>
                      <th scope="col" class="text-left">Frequency</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="fee in props.row.fees" :key="fee.label">
                      <td>{{ fee.label }}</td>
                      <td>{{ formatMoney(fee.amount) }}</td>
                      <td v-if="fee.frequency === 'once'">One-Time</td>
                      <td v-else-if="fee.frequency === 'month'">Monthly</td>
                      <td v-else-if="fee.frequency === 'year'">Annual</td>
                      <td v-else>Unknown</td>
                    </tr>
                  </tbody>
                </q-markup-table>
              </q-menu>
            </q-btn>
            <div v-else>{{ col.value }}</div>
          </q-td>
        </q-tr>
        <q-tr
          v-if="props.rowIndex === rows.length - 1"
          class="text-bold text-center title-row"
          :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'"
        >
          <q-td class="text-left">
            <div>
              Comp Average
              <q-icon v-if="subjectDetail && !printingMode" name="info_outline">
                <q-tooltip>
                  Does not include {{ subjectDetail.building_name || subjectDetail.street_address }}
                </q-tooltip>
              </q-icon>
            </div>
          </q-td>
          <q-td v-for="(val, i) in footerRow" :key="i">
            {{ val }}
          </q-td>
        </q-tr>
      </template>
    </q-table>
  </property-report-card>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import type { QTableColumn } from 'quasar'
import type {
  PropertyDetailsResponse,
  Availability,
  AvailabilityPeriod,
  Price,
  AdditionalFee
} from 'common'
import { formatMoney, unitsFound } from '@/utils'
import PropertyReportCard from '@/components/PropertyReportCard.vue'

export default {
  name: 'PropertyCompareFees',
  components: { PropertyReportCard },
  props: {
    filteredProperties: {
      type: Array<{
        prop: PropertyDetailsResponse
        units: {
          unit: Availability
          lastHistory: Price
          period: AvailabilityPeriod
        }[]
      }>,
      required: true
    },
    subjectDetail: {
      type: Object as PropType<PropertyDetailsResponse | null>,
      default: null
    },
    printingMode: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    footerRow(): (string | null)[] {
      if (this.columns) {
        return this.columns.slice(1).map((col) => {
          const strVals = this.rows
            .filter((_) => _.id !== this.subjectDetail?.id)
            .map((row) => (col.format ? col.format(row[col.field], row) : row[col.field]))
            .filter((_) => _)
            .map(String)
          const vals = strVals
            .map((_) =>
              _.split('$').join('').replace('Cat', '').replace('Dog', '').split(',').join('')
            )
            .flatMap((_) => _.split('/'))
            .map(parseFloat)
            .filter((_) => !isNaN(_))
          if (!vals.length) {
            return null
          }
          return formatMoney(Math.round(vals.reduce((a, b) => a + b) / vals.length))
        })
      }
      return []
    },
    rows() {
      return this.filteredProperties.flatMap(({ prop }) => ({
        id: prop.id,
        label: prop.building_name || prop.street_address,
        application_fee: prop.application_fee || null,
        admin_fee: prop.admin_fee || null,
        amenity_fee: prop.amenity_fee || null,
        storage_fee: prop.storage_fee || null,
        pet_rent: [prop.cats_monthly_rent || null, prop.dogs_monthly_rent || null],
        pet_one_time_fee: [prop.cats_one_time_fee || null, prop.dogs_one_time_fee || null],
        pet_deposit: [prop.cats_deposit || null, prop.dogs_deposit || null],
        parking_covered: prop.parking_covered || null,
        parking_garage: prop.parking_garage || null,
        parking_surface_lot: prop.parking_surface_lot || null,
        fees: prop.fees || null
      }))
    },
    columns() {
      return [
        { name: '', label: '', field: 'label', align: 'left', classes: 'constrained-cell' },
        {
          name: 'Application Fee',
          label: 'Application Fee',
          field: 'application_fee',
          align: 'center',
          sortable: true,
          sortOrder: 'da',
          format: (x: number | null) => formatMoney(x)
        },
        {
          name: 'Admin Fee',
          label: 'Admin Fee',
          field: 'admin_fee',
          align: 'center',
          sortable: true,
          sortOrder: 'da',
          format: (x: number | null) => formatMoney(x)
        },
        {
          name: 'Amenity Fee',
          label: 'Amenity Fee',
          field: 'amenity_fee',
          align: 'center',
          sortable: true,
          sortOrder: 'da',
          format: (x: number | null) => formatMoney(x)
        },
        {
          name: 'Storage Fee',
          label: 'Storage Fee',
          field: 'storage_fee',
          align: 'center',
          sortable: true,
          sortOrder: 'da',
          format: (x: number | null) => formatMoney(x)
        },
        {
          name: 'Pet Rent',
          label: 'Pet Rent',
          field: (row) => row.pet_rent[0],
          align: 'center',
          sortable: true,
          sortOrder: 'da',
          format: (_, row) => this.formatPets(row.pet_rent)
        },
        {
          name: 'Pet Fee',
          label: 'Pet Fee',
          field: (row) => row.pet_one_time_fee[0],
          align: 'center',
          sortable: true,
          sortOrder: 'da',
          format: (_, row) => this.formatPets(row.pet_one_time_fee)
        },
        {
          name: 'Pet Deposit',
          label: 'Pet Deposit',
          field: (row) => row.pet_deposit[0],
          align: 'center',
          sortable: true,
          sortOrder: 'da',
          format: (_, row) => this.formatPets(row.pet_deposit)
        },
        {
          name: 'Covered Parking',
          label: 'Covered Parking',
          field: 'parking_covered',
          align: 'center',
          sortable: true,
          sortOrder: 'da',
          format: (x: number | null) => formatMoney(x)
        },
        {
          name: 'Garage Parking',
          label: 'Garage Parking',
          field: 'parking_garage',
          align: 'center',
          sortable: true,
          sortOrder: 'da',
          format: (x: number | null) => formatMoney(x)
        },
        {
          name: 'Surface Lot Parking',
          label: 'Surface Lot Parking',
          field: 'parking_surface_lot',
          align: 'center',
          sortable: true,
          sortOrder: 'da',
          format: (x: number | null) => formatMoney(x)
        },
        {
          name: 'fees',
          label: 'More Fees',
          field: 'fees',
          align: 'center',
          sortable: false,
          format: (fees: AdditionalFee[] | null) => {
            if (fees && fees.length) {
              // This is used in the CSV export, not for the UI
              return fees.reduce(
                (acc, { label, amount }) => {
                  acc[label] = formatMoney(amount, 0)
                  return acc
                },
                {} as { [label: string]: string }
              )
            }
            return fees
          }
        }
      ] as QTableColumn[]
    }
  },
  methods: {
    formatMoney,
    unitsFound,
    formatPets(range: [number | null, number | null]): string {
      if (range[0] === range[1]) {
        return formatMoney(range[0])
      }
      return `${formatMoney(range[0])} Cat / ${formatMoney(range[1])} Dog`
    },
    exportCSV() {
      const feeHeaders = this.rows.flatMap((row) => row.fees?.map((_) => _.label) || [])
      const headers = this.columns
        .map((col) => col.label)
        .filter((_) => _ !== 'More Fees')
        .concat(feeHeaders)
      const csv = this.rows
        .map((row) =>
          this.columns
            .flatMap((col) => {
              if (col.field === 'fees') {
                const lookup = col.format(row[col.field], row) || {}
                return feeHeaders.map((_) => lookup[_])
              }
              return col.format ? col.format(row[col.field], row) : row[col.field]
            })
            .join(',')
        )
        .join('\n')
      const blob = new Blob([headers.join(',') + '\n' + csv], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `hello-data-fees-${new Date().toISOString().split('T')[0]}.csv`
      a.click()
    }
  }
}
</script>
<style scoped>
.title-row td {
  border-top: 2px solid rgba(0, 0, 0, 0.12);
}
.constrained-cell {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
